import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  PlusIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  getMentionedSentences,
  getUserNames,
  groupActionItems,
  parseActionItems,
  parseSummaryText,
} from '../utils/transcript';

import type { FirefliesTranscript } from '../types';
import type {
  ActionItemGroup,
  SummaryBlock,
} from '../utils/transcript';

import Modal from '@/common/components/Modal';

interface TranscriptDetailModalProps {
  transcript: FirefliesTranscript | null;
  userEmail?: string;
  isOpen: boolean;
  onClose: () => void;
  onSaveToNotes?: (transcript: FirefliesTranscript) => void;
  onAddActionToChecklist?: (actionItem: string) => void;
  hasNotesWidget?: boolean;
  hasChecklistWidget?: boolean;
}

export default function TranscriptDetailModal({
  transcript,
  userEmail,
  isOpen,
  onClose,
  onSaveToNotes,
  onAddActionToChecklist,
  hasNotesWidget,
  hasChecklistWidget,
}: TranscriptDetailModalProps) {
  const { t } = useTranslation();

  if (!transcript) return null;

  const overview = transcript.summary?.overview ?? transcript.summary?.short_summary ?? '';
  const summaryBlocks = parseSummaryText(overview);
  const parsed = parseActionItems(transcript.summary?.action_items);
  const names = getUserNames(userEmail ?? '', transcript.meeting_attendees);
  const actionGroups = groupActionItems(parsed, names);
  const userGroups = actionGroups.filter((g) => g.isCurrentUser);
  const mentionedSentences = getMentionedSentences(transcript, userEmail ?? '');
  const topics = transcript.summary?.topics_discussed ?? transcript.summary?.keywords ?? [];
  const date = transcript.date ? new Date(transcript.date) : new Date();

  return (
    <Modal isOpen={isOpen} size="xl" title={transcript.title} onClose={onClose}>
      <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{format(date, 'PPp')}</span>
          {transcript.duration && (
            <span className="before:content-['·'] before:mr-3">
              {Math.round(transcript.duration)} min
            </span>
          )}
          {transcript.participants && transcript.participants.length > 0 && (
            <span className="before:content-['·'] before:mr-3">
              {transcript.participants.length} participants
            </span>
          )}
          <a
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline ml-auto text-xs"
            href={transcript.transcript_url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('fireflies.viewTranscript')}
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
          </a>
        </div>

        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {topics.map((topic) => (
              <span
                key={topic}
                className="px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full"
              >
                {topic}
              </span>
            ))}
          </div>
        )}

        {summaryBlocks.length > 0 && (
          <section>
            <SectionHeading>Summary</SectionHeading>
            <SummaryContent blocks={summaryBlocks} />
          </section>
        )}

        {userGroups.length > 0 && (
          <section>
            <SectionHeading>
              {t('fireflies.actionItems')}
              <span className="ml-2 text-xs font-normal text-gray-400">
                {userGroups.reduce((sum, g) => sum + g.items.length, 0)}
              </span>
            </SectionHeading>

            <div className="space-y-3">
              {userGroups.map((group) => (
                <ActionGroupSection
                  key={group.person || '_ungrouped'}
                  group={group}
                  hasChecklistWidget={hasChecklistWidget}
                  onAdd={onAddActionToChecklist}
                />
              ))}
            </div>
          </section>
        )}

        {mentionedSentences.length > 0 && (
          <section>
            <SectionHeading>{t('fireflies.myMentions')}</SectionHeading>
            <div className="space-y-1.5">
              {mentionedSentences.map((s) => (
                <div
                  key={s.index}
                  className="px-3 py-2 bg-blue-50/60 border border-blue-100 rounded-lg text-sm text-gray-700"
                >
                  <span className="font-medium text-blue-700">{s.speaker_name}:</span>{' '}
                  {s.text}
                </div>
              ))}
            </div>
          </section>
        )}

        {hasNotesWidget && onSaveToNotes && (
          <div className="pt-2 border-t border-gray-100">
            <button
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              onClick={() => onSaveToNotes(transcript)}
            >
              {t('fireflies.saveSummaryAsNote')}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
    {children}
  </h4>
);

const SummaryContent = ({ blocks }: { blocks: SummaryBlock[] }) => {
  const hasBullets = blocks.some((b) => b.type === 'bullet');

  if (!hasBullets) {
    return (
      <div className="text-sm text-gray-600 leading-relaxed space-y-2">
        {blocks.map((block, i) => (
          <p key={i}>
            <InlineSegments segments={block.segments} />
          </p>
        ))}
      </div>
    );
  }

  const paragraphs: SummaryBlock[] = [];
  const bullets: SummaryBlock[] = [];
  blocks.forEach((b) => (b.type === 'bullet' ? bullets : paragraphs).push(b));

  return (
    <div className="space-y-3">
      {paragraphs.length > 0 && (
        <div className="text-sm text-gray-600 leading-relaxed space-y-2">
          {paragraphs.map((block, i) => (
            <p key={i}>
              <InlineSegments segments={block.segments} />
            </p>
          ))}
        </div>
      )}
      <ul className="space-y-2">
        {bullets.map((block, i) => (
          <li
            key={i}
            className="flex gap-2.5 text-sm text-gray-600 leading-relaxed"
          >
            <span className="text-gray-300 mt-0.5 shrink-0">•</span>
            <span>
              <InlineSegments segments={block.segments} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const InlineSegments = ({ segments }: { segments: { type: string; value: string }[] }) => (
  <>
    {segments.map((seg, i) =>
      seg.type === 'bold' ? (
        <strong key={i} className="font-semibold text-gray-800">
          {seg.value}
        </strong>
      ) : (
        <React.Fragment key={i}>{seg.value}</React.Fragment>
      ),
    )}
  </>
);

const ActionGroupSection = ({
  group,
  hasChecklistWidget,
  onAdd,
}: {
  group: ActionItemGroup;
  hasChecklistWidget?: boolean;
  onAdd?: (item: string) => void;
}) => (
  <div
    className={`rounded-lg overflow-hidden ${
      group.isCurrentUser
        ? 'bg-amber-50/70 border border-amber-200'
        : 'bg-gray-50/70 border border-gray-100'
    }`}
  >
    {group.person && (
      <div
        className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold ${
          group.isCurrentUser
            ? 'text-amber-700 bg-amber-100/50'
            : 'text-gray-500 bg-gray-100/50'
        }`}
      >
        <UserIcon className="w-3.5 h-3.5" />
        {group.person}
        {group.isCurrentUser && (
          <span className="ml-1 text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-200/60 px-1.5 py-0.5 rounded">
            You
          </span>
        )}
      </div>
    )}
    <ul className="divide-y divide-gray-100">
      {group.items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 px-3 py-2">
          <CheckCircleIcon
            className={`w-4 h-4 mt-0.5 shrink-0 ${
              group.isCurrentUser ? 'text-amber-400' : 'text-gray-300'
            }`}
          />
          <span className="flex-1 text-sm text-gray-700 leading-snug">{item.text}</span>
          {item.timestamp && (
            <span className="text-[11px] text-gray-400 tabular-nums shrink-0 mt-0.5">
              {item.timestamp}
            </span>
          )}
          {hasChecklistWidget && onAdd && (
            <button
              className={`shrink-0 p-1 rounded transition-colors ${
                group.isCurrentUser
                  ? 'text-amber-400 hover:text-amber-600 hover:bg-amber-100'
                  : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
              }`}
              title="Add to checklist"
              onClick={() => onAdd(item.text)}
            >
              <PlusIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </li>
      ))}
    </ul>
  </div>
);
